import { useCallback, useEffect, useRef, useState } from 'react';

import { EVALUATION_TYPE_ORDER } from './evaluationSelection';
import { getPersistedTemplateId } from './templateOptions';

import type { EvaluationElementsSelection } from './evaluationSelection';
import type { EvaluationTypeValue } from '@/constants/cycle-evaluation/CycleEvaluation';
import type { CycleEvaluation } from '@/types/cycle-evaluation/CycleEvaluation';
import type { CycleEvaluationGroup } from '@/types/cycle-evaluation/CycleEvaluationGroup';

import { EvaluationType } from '@/constants/cycle-evaluation/CycleEvaluation';
import { groupIncludesEvaluationType } from '@/navigation/CycleEvaluation/cycleEvaluationGroupSteps';

const EMPTY_SELECTION: EvaluationElementsSelection = {
    managerAssessment: false,
    feedback360: false,
    selfAssessment: false,

    assessOrganizationalValues: true,
    templateIds: {},
};

const SELECTION_KEY_BY_TYPE: Record<
    EvaluationTypeValue,
    keyof Pick<EvaluationElementsSelection, 'managerAssessment' | 'feedback360' | 'selfAssessment'>
> = {
    [EvaluationType.MANAGER_EVALUATION]: 'managerAssessment',
    [EvaluationType.EVALUATION_360]: 'feedback360',
    [EvaluationType.SELF_EVALUATION]: 'selfAssessment',
};

/**
 * @param canSelectManagerAssessment False when Manager Assessment is not shown for the cycle (see
 * `useManagerAssessmentAccess`): the company's plan does not include it and the cycle has none.
 * A cycle that already has one keeps it selected, so saving the step never removes it.
 */
export const useEvaluationElementsSelection = (
    group: CycleEvaluationGroup | undefined,
    cycleEvaluationByType: Partial<Record<EvaluationTypeValue, CycleEvaluation>>,
    canSelectManagerAssessment = true,
) => {
    const [selection, setSelection] = useState<EvaluationElementsSelection>(EMPTY_SELECTION);

    const hydratedRef = useRef(false);

    useEffect(() => {
        if (!group || hydratedRef.current) {
            return;
        }

        hydratedRef.current = true;

        setSelection((current) => ({
            ...current,
            managerAssessment:
                canSelectManagerAssessment &&
                groupIncludesEvaluationType(group, EvaluationType.MANAGER_EVALUATION),
            feedback360: groupIncludesEvaluationType(group, EvaluationType.EVALUATION_360),
            selfAssessment: groupIncludesEvaluationType(group, EvaluationType.SELF_EVALUATION),
        }));
    }, [group, canSelectManagerAssessment]);

    const hydratedOrganizationalValuesRef = useRef(false);

    // "Assess organizational value" is stored on the manager evaluation, so it can only be restored
    // once that evaluation has loaded — separate from the group hydration above, which runs earlier.
    useEffect(() => {
        const managerEvaluation = cycleEvaluationByType[EvaluationType.MANAGER_EVALUATION];

        if (!managerEvaluation || hydratedOrganizationalValuesRef.current) {
            return;
        }

        hydratedOrganizationalValuesRef.current = true;

        setSelection((current) => ({
            ...current,
            assessOrganizationalValues: managerEvaluation.assessOrganizationalValues ?? false,
        }));
    }, [cycleEvaluationByType]);

    const hydratedTemplateTypesRef = useRef(new Set<EvaluationTypeValue>());

    useEffect(() => {
        const restored: Partial<Record<EvaluationTypeValue, string>> = {};

        EVALUATION_TYPE_ORDER.forEach((type) => {
            const questionnaire = cycleEvaluationByType[type]?.cycleQuestionnaire;

            if (!questionnaire || hydratedTemplateTypesRef.current.has(type)) {
                return;
            }

            hydratedTemplateTypesRef.current.add(type);

            const persistedTemplateId = getPersistedTemplateId(questionnaire);

            if (persistedTemplateId) {
                restored[type] = persistedTemplateId;
            }
        });

        if (Object.keys(restored).length === 0) {
            return;
        }

        setSelection((current) => ({
            ...current,
            templateIds: { ...restored, ...current.templateIds },
        }));
    }, [cycleEvaluationByType]);

    const handleToggleType = useCallback((type: EvaluationTypeValue) => {
        setSelection((current) => {
            const key = SELECTION_KEY_BY_TYPE[type];

            return { ...current, [key]: !current[key] };
        });
    }, []);

    const handleTemplateChange = useCallback((type: EvaluationTypeValue, templateId: string) => {
        setSelection((current) => ({
            ...current,
            templateIds: { ...current.templateIds, [type]: templateId },
        }));
    }, []);

    const handleToggleOrganizationalValues = useCallback(() => {
        setSelection((current) => ({
            ...current,
            assessOrganizationalValues: !current.assessOrganizationalValues,
        }));
    }, []);

    return {
        selection,
        handleToggleType,
        handleTemplateChange,
        handleToggleOrganizationalValues,
    };
};
